import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import {
  showScreenTransitionOverlay,
  hideScreenTransitionOverlay,
  paintThen,
} from '../helper/screenTransitionOverlay';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import GoogleCalendarOAuthCallback from './GoogleCalendarOAuthCallback.vue';
import GoogleAdsOAuthCallback from './GoogleAdsOAuthCallback.vue';
import ClaudeOAuthConsent from './ClaudeOAuthConsent.vue';

const routes = [
  ...dashboard.routes,
  {
    path: '/google-calendar/callback',
    name: 'google_calendar_oauth_callback',
    component: GoogleCalendarOAuthCallback,
    meta: { requiresAuth: false },
  },
  // Consentimento do login do Claude (conector MCP): tela do usuário, sem empresa na URL.
  {
    path: frontendURL('oauth/claude'),
    name: 'claude_oauth_consent',
    component: ClaudeOAuthConsent,
    meta: { userLevel: true, returnAfterLogin: true },
  },
  {
    path: '/google-ads/callback',
    name: 'google_ads_oauth_callback',
    component: GoogleAdsOAuthCallback,
    meta: { requiresAuth: false },
  },
];

export const router = createRouter({ history: createWebHistory(), routes });

// Auto-recuperação de DEPLOY: quando publicamos uma versão nova, os chunks mudam
// de hash; quem está com o index antigo (cache/aba aberta) pede um chunk que não
// existe mais -> "Failed to fetch dynamically imported module". Em vez de quebrar
// a tela do cliente, recarregamos pra pegar a versão atual. Guarda contra loop:
// só 1 reload a cada 15s.
const isChunkLoadError = error => {
  const msg = (error && error.message) || '';
  return /dynamically imported module|module script failed|error loading dynamically|Failed to fetch dynamically|ChunkLoadError/i.test(
    msg
  );
};

const reloadForNewVersion = () => {
  try {
    const KEY = 'app-chunk-reload-at';
    const last = Number(window.sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 15000) return; // já recarregou há pouco -> evita loop
    window.sessionStorage.setItem(KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage indisponível: segue pro reload mesmo assim
  }
  window.location.reload();
};

router.onError(error => {
  if (isChunkLoadError(error)) reloadForNewVersion();
});

// Vite dispara este evento quando um preload de módulo falha (deploy novo).
window.addEventListener('vite:preloadError', event => {
  if (event && typeof event.preventDefault === 'function')
    event.preventDefault();
  reloadForNewVersion();
});

export const RETURN_AFTER_LOGIN_KEY = 'cs-return-after-login';

export const validateAuthenticateRoutePermission = (to, next) => {
  // Skip auth for routes that don't require it (e.g. OAuth callbacks)
  if (to.meta?.requiresAuth === false) {
    return next();
  }

  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    // O login é outra aplicação e volta para /app: guarda onde o usuário ia
    // (ex.: o Claude pedindo autorização) para retomar depois de entrar.
    if (to.meta?.returnAfterLogin) {
      window.sessionStorage.setItem(RETURN_AFTER_LOGIN_KEY, to.fullPath);
    }
    window.location.assign('/app/login');
    return '';
  }

  const pendingReturn = window.sessionStorage.getItem(RETURN_AFTER_LOGIN_KEY);
  if (pendingReturn) {
    window.sessionStorage.removeItem(RETURN_AFTER_LOGIN_KEY);
    if (pendingReturn !== to.fullPath) return next(pendingReturn);
  }

  if (to.meta?.userLevel) return next();

  if (!to.name) {
    // Admins go to Dashboard, agents go to Conversations
    const isAdmin = user.role === 'administrator';
    const defaultRoute = isAdmin ? 'overview' : 'dashboard';
    return next(frontendURL(`accounts/${user.account_id}/${defaultRoute}`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  // Resposta IMEDIATA ao trocar de tela: o overlay de transição pinta ANTES da
  // montagem pesada da tela nova travar a main thread (as telas grandes são
  // eager — o custo é mount, e spinner interno só pintaria tarde demais).
  // Some após a primeira pintura da tela nova (double-rAF pós afterEach).
  router.afterEach(() => {
    requestAnimationFrame(() =>
      requestAnimationFrame(hideScreenTransitionOverlay)
    );
  });
  router.onError(() => hideScreenTransitionOverlay());

  router.beforeEach(async (to, _from, next) => {
    // Só em troca REAL de tela (não na carga inicial nem em mudança de params).
    if (_from.name && to.name && to.name !== _from.name) {
      showScreenTransitionOverlay();
      await paintThen();
    }
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    // `await` obrigatório: o guard é async e declara `next`, então o vue-router
    // só considera a navegação válida se `next` já tiver sido chamado quando
    // esta promise resolver. Deixar o `.then` solto resolvia o guard antes da
    // autenticação responder -> "Invalid navigation guard" e tela em branco.
    await userAuthentication;
    return validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
